// The FDE List — role config + data snapshot (see README.md for the shape).
// Every figure traces to a posted salary band, a live careers page, levels.fyi,
// or a dated news source. Verify against live postings when refreshing:
// ranges and hiring statuses move fast in this market.
//
// To refresh: update lastUpdated, re-verify baseSalary/totalComp + hiringStatus
// against each applyUrl, and adjust scores (0–10) if a program's trajectory
// changed. Ranks and tiers are editorial; keep tiers contiguous by rank.

import {fmtRange} from './format';

export const marketStats = [
  {value: '42×', label: 'Posting growth ’23–’25'},
  {value: '$190k', label: 'Median base salary (US)'},
  {value: '4,900+', label: 'Open US roles'},
  {value: '$550k', label: 'Top-of-band total comp'},
];

export const methodology = [
  {
    weight: '30%',
    name: 'Compensation',
    description: 'Posted salary bands, levels.fyi data, and verified offer reports — total comp where equity data exists, base bands otherwise.',
  },
  {
    weight: '25%',
    name: 'Program maturity',
    description: 'How real the FDE org is: age, scale, training, leadership, and whether forward-deployed work sits at the core of the business.',
  },
  {
    weight: '25%',
    name: 'Career capital',
    description: 'What the role does for the rest of your career — brand strength, alumni outcomes, and the caliber of problems you get to claim.',
  },
  {
    weight: '20%',
    name: 'Momentum',
    description: 'Open req counts, growth trajectory, and strategic investment — is the program expanding or coasting?',
  },
];

export const excludedNote = 'We also track Meta, NVIDIA, and SpaceXAI (formerly xAI), currently excluded from the ranking: Meta began posting Enterprise AI FDE reqs in August 2026 but the program is too nascent to score, NVIDIA’s first FDE-titled req (Senior Staff, posted August 2026 at a $224k–$356.5k band) is a single opening inside a Solutions-Architect-led motion, and xAI’s lone FDE req disappeared after the SpaceX merger — no FDE-titled roles remain on any SpaceXAI board. Snowflake, excluded in July, rejoined the ranking when its FDE reqs reopened.';

export const faqItems = [
  {
    question: 'What is a forward deployed engineer (FDE)?',
    answer: 'A forward deployed engineer is a software engineer who embeds directly with customers to ship production software against their specific problems — part engineer, part consultant, part product manager. Palantir invented the role around 2005; the enterprise-AI wave of 2024–2026 turned it into one of the most in-demand titles in tech, because making AI work inside a large company is as much a deployment problem as a modeling one.',
  },
  {
    question: 'How much do forward deployed engineers make?',
    answer: 'Across 135 US FDE postings analyzed in mid-2026, the median base salary was about $190k, with most roles between $160k and $220k. Top programs pay far more: Anthropic’s posted US band runs $280k–$320k, OpenAI’s FDE-family postings reach $325k, and estimated total compensation at the frontier labs runs $350k–$550k. Nearly all FDE roles add equity on top, and essentially none carry a sales quota.',
  },
  {
    question: 'Is FDE really the fastest-growing job in tech?',
    answer: 'By several measures, yes. LinkedIn found forward-deployed positions grew 42× from 2023 to 2025 — the fastest-growing AI-era job title (AI engineer grew 13× over the same window) — and Indeed postings grew roughly 8× year over year into 2026. The wave accelerated in mid-2026 when AWS ($1B) and Microsoft ($2.5B) stood up dedicated FDE organizations within a single week of each other.',
  },
  {
    question: 'What skills do FDE roles require?',
    answer: 'Strong full-stack engineering (Python and TypeScript dominate postings), production LLM experience — agents, RAG, evals, prompting — and genuine customer-facing ability: scoping ambiguous problems, running working sessions with executives, and demoing under pressure. An analysis of 1,000 postings found 45% of FDEs come from software engineering backgrounds, 22% from solutions engineering, and about 8% are ex-founders.',
  },
  {
    question: 'Do forward deployed engineers travel a lot?',
    answer: 'Usually. Most programs expect 25–50% travel to customer sites, and public-sector deployments (Palantir, Scale AI) can exceed that or require working from secure facilities. A handful of programs are remote-friendly — Databricks, Cursor, Cohere, and some Salesforce reqs — with periodic on-site embeds instead of constant travel.',
  },
  {
    question: 'What’s the difference between an FDE and a solutions architect or sales engineer?',
    answer: 'FDEs ship production code and own outcomes after the contract is signed; solutions architects and sales engineers primarily advise, demo, and influence the deal before it closes. FDE roles are compensated like engineering — an analysis of 1,000 postings found 0% carry quotas. Adjacent roles such as NVIDIA’s Solutions Architects overlap heavily in skills but stop short of owning production delivery.',
  },
  {
    question: 'Can new grads become forward deployed engineers?',
    answer: 'A few real onramps exist. Palantir runs the industry’s deepest new-grad and intern FDE pipeline; Sierra opened a “Software Engineer, Agent (New Grad 2027)” track in August 2026 with a posted $150k–$180k band; and Salesforce posts associate-level FDE roles. Mistral runs one of the only FDE internships (Paris). Google’s early-career FDE I band and IBM’s entry-level track — both onramps earlier in 2026 — are currently unposted. Most other programs want 3–7 years of experience plus production LLM work.',
  },
  {
    question: 'How do I prepare for FDE interviews?',
    answer: 'Expect a hybrid loop: practical coding (often building against ambiguous, production-style requirements), AI system design with real-world constraints like token costs and eval gates, and — the differentiator — customer-simulation rounds where you scope a problem live with a non-technical “executive.” Palantir’s decomposition round and Anthropic’s customer conversation are the archetypes. Practice narrating decisions while you build; that is the actual job.',
  },
];

const fdeJobs = {
  lastUpdated: '2026-09-02',
  jobs: [{
    rank: 1,
    tier: 'S',
    company: 'OpenAI',
    slug: 'openai',
    guideSlug: 'openai-forward-deployed-engineer-interview',
    companyDomain: 'openai.com',
    roleTitle: 'Forward Deployed Engineer',
    headline: 'The hottest FDE program in tech — Fortune 500 rollouts backed by a $4B deployment company.',
    description: 'FDEs in OpenAI’s Model Deployment for Business org own frontier-model deployments end to end — discovery, scoping, full-stack builds on OpenAI APIs, and production rollout — for strategic enterprise and government customers. What they learn in the field feeds directly back into research and product.',
    baseSalary: {min: 153000, max: 325000},
    totalComp: {min: 350000, max: 550000},
    compNote: 'Base: official postings, September 2026 ($162k–$280k core FDE; Forward Deployed Software Engineer reqs $153k–$325k; OpenAI Deployment Company US FDE $170k–$400k), plus PPU equity. Total comp: third-party estimate (2026 FDE comp reports) — OpenAI equity is substantial but variable.',
    locations: ['San Francisco', 'New York', 'Seattle', 'Washington DC', '+ Tokyo, Singapore, Seoul, Sydney, and DeployCo hubs in London and Paris'],
    locationSummary: 'SF · NYC · 10+ cities',
    arrangement: 'Hybrid, 3 days in office · up to 50% travel',
    experience: '5+ years, customer-facing + production LLM work',
    newGradFriendly: false,
    remoteFriendly: false,
    activelyHiring: true,
    hiringStatus: 'Aggressively hiring on two boards — ~23 FDE-titled roles incl. new Healthcare and Legal tracks, plus Deployment Company reqs in five regions',
    interviewProcess: [
      'Recruiter screen focused on FDE-specific motivation',
      'Practical technical screen or take-home, LLM-product flavored',
      'Coding rounds built on agentic and eval scenarios',
      'System design with token-cost, latency, and eval-gate constraints',
      'Customer role-play with a simulated non-technical executive, plus behavioral',
      'Leadership final — about 3–4 weeks end to end',
    ],
    programScale: 'Org built 2024–25; expanded May 2026 via the $4B OpenAI Deployment Company JV',
    notableFact: 'OpenAI spun up an entire company for this: the OpenAI Deployment Company (May 2026), backed by TPG, Bain Capital, and Goldman Sachs, embeds FDEs directly into Fortune 500s — and has grown FDE headcount into the hundreds via the Tomoro and Northslope acquisitions.',
    applyUrl: 'https://jobs.ashbyhq.com/openai',
    scores: {
      comp: 10, program: 8, brand: 10, momentum: 10,
    },
  }, {
    rank: 2,
    tier: 'S',
    company: 'Palantir',
    slug: 'palantir',
    guideSlug: 'palantir-forward-deployed-engineer-interview',
    companyDomain: 'palantir.com',
    roleTitle: 'Forward Deployed Software Engineer',
    headline: 'The original. Twenty years of FDEs embedded everywhere from factory floors to fighter programs.',
    description: 'Palantir invented the FDE model. FDSEs embed at customer sites building on Foundry, Gotham, and AIP for defense, intelligence, hospitals, manufacturers, and banks — with the paired Deployment Strategist track handling the operational side of the same deployments.',
    baseSalary: {min: 135000, max: 200000},
    totalComp: {min: 155000, max: 295000},
    compNote: 'Base: official postings, September 2026 (NY/DC band; new grads $135k–$145k). Total comp: levels.fyi FDSE range, median ≈ $208k. Forward Deployed Infrastructure band $110k–$147k base.',
    locations: ['New York', 'Washington DC', 'Palo Alto', 'Seattle', '+ 10 US and global offices'],
    locationSummary: 'NYC · DC · 14+ cities',
    arrangement: 'Office-first · up to 25% travel, more when deployed',
    experience: 'Intern → senior — the only true new-grad FDE pipeline',
    newGradFriendly: true,
    remoteFriendly: false,
    activelyHiring: true,
    hiringStatus: '76 open FDE-family roles (49 in the US), including new-grad and intern tracks',
    interviewProcess: [
      'Recruiter screen — motivation and fit are filtered hard',
      'HackerRank assessment: implementation-heavy coding plus SQL',
      'The signature “decomp” round: decompose a vague real-world system',
      'Further rounds: coding, system design, re-engineering',
      'Hiring manager final',
    ],
    programScale: 'Two decades old and still the core of Palantir’s delivery model — the largest dedicated FDE org anywhere',
    notableFact: 'The FDE-to-founder pipeline is real: Palantir alumni have founded 111+ companies that raised $11.6B+, including Anduril. Until ~2016, Palantir deployed more FDEs than it had core software engineers.',
    applyUrl: 'https://jobs.lever.co/palantir',
    scores: {
      comp: 7, program: 10, brand: 10, momentum: 8,
    },
  }, {
    rank: 3,
    tier: 'S',
    company: 'Anthropic',
    slug: 'anthropic',
    guideSlug: 'anthropic-forward-deployed-engineer-interview',
    companyDomain: 'anthropic.com',
    roleTitle: 'Forward Deployed Engineer',
    headline: 'Top-of-market pay to ship production Claude systems — and the hiring window is open again.',
    description: 'Anthropic FDEs embed with strategic customers to ship production Claude applications — building agents and MCP servers, providing white-glove deployment support, and feeding repeatable patterns back to product. Roughly 40% full-stack build, 30% architecture, 30% discovery.',
    baseSalary: {min: 280000, max: 320000},
    totalComp: {min: 350000, max: 550000},
    compNote: 'Base: official posted US band, September 2026 ($280k–$320k, inclusive of target bonus/commission; FDE managers to $405k). Total comp: third-party estimate; packages are RSU-heavy. The Applied AI Architect track ($240k–$315k OTE) hires continuously.',
    locations: ['San Francisco', 'New York', 'Seattle', '+ London, Munich, Paris'],
    locationSummary: 'SF · NYC · 6+ cities',
    arrangement: 'Hybrid, 25%+ in office · 25–50% travel',
    experience: '3–4+ years customer-facing engineering + production LLM work',
    newGradFriendly: false,
    remoteFriendly: false,
    activelyHiring: true,
    hiringStatus: 'FDE window open since Aug 13 — IC reqs in NYC/SF/Seattle plus new Munich and Paris postings; FDE managers hiring in NYC and London',
    interviewProcess: [
      'Recruiter screen, including mission alignment',
      'Technical screen on a Claude deployment scenario',
      'Live coding or take-home — constraints change mid-exercise, like real customers',
      'Customer-conversation simulation (reportedly the decisive round)',
      'Virtual onsite: system design, technical deep dive, values — 4–6 weeks total',
    ],
    programScale: 'FDE function launched fall 2025 inside Applied AI; now expanding into EMEA (Munich, Paris, London) and extends through partners — Deloitte posts badged “Anthropic FDE” roles',
    notableFact: 'The highest posted floor of any FDE program ($280k–$320k US band). FDE windows historically fill within weeks — the current one opened August 13, 2026.',
    applyUrl: 'https://job-boards.greenhouse.io/anthropic',
    scores: {
      comp: 10, program: 7, brand: 10, momentum: 7,
    },
  }, {
    rank: 4,
    tier: 'A',
    company: 'Google',
    slug: 'google',
    guideSlug: 'google-forward-deployed-engineer-interview',
    companyDomain: 'google.com',
    roleTitle: 'Forward Deployed Engineer, Google Cloud',
    headline: 'The only formally leveled FDE ladder — posted bands to $364k at Partner level, with DeepMind on call.',
    description: 'Google Cloud FDEs are “embedded builders” who code, debug, and ship bespoke agentic solutions inside customer environments, with direct access to DeepMind engineering. A separate DeepMind FDE track embeds with strategic partners on evals and frontier deployments.',
    baseSalary: {min: 174000, max: 364000},
    totalComp: null,
    compNote: 'Base: official posted per-level bands, September 2026 — FDE III $174k–$252k through Partner FDE V $262k–$364k, plus 15–25% bonus targets and equity. FDE I reqs (posted $102k–$145k in July 2026) are not currently live. Google doesn’t publish FDE total-comp figures.',
    locations: ['New York', 'Reston', 'Mountain View', 'Chicago', 'London', 'Tokyo', 'Sydney'],
    locationSummary: 'NYC · global hubs',
    arrangement: 'Hybrid hubs · substantial time in customer environments',
    experience: 'Leveled ladder — currently hiring levels II through Partner V',
    newGradFriendly: false,
    remoteFriendly: false,
    activelyHiring: true,
    hiringStatus: '~40 FDE reqs live across levels II–V, plus DeepMind FDE',
    interviewProcess: [
      'Recruiter screen',
      'Practical “vibe coding” session against ambiguous, production-style requirements',
      'AI system design: RAG, vector stores, agents at scale',
      'Googleyness and behavioral rounds (GCA framework)',
    ],
    programScale: 'FDEs predate the 2026 push, but Thomas Kurian is now scaling an “AI deployment army” backed by a $750M partner commitment',
    notableFact: 'Google Cloud’s CEO personally recruits FDEs on stage — and it’s the only company with public per-level FDE pay bands.',
    applyUrl: 'https://www.google.com/about/careers/applications/jobs/results/?q=%22Forward%20Deployed%20Engineer%22',
    scores: {
      comp: 8, program: 7, brand: 9, momentum: 9,
    },
  }, {
    rank: 5,
    tier: 'A',
    company: 'Databricks',
    slug: 'databricks',
    guideSlug: 'databricks-forward-deployed-engineer-interview',
    companyDomain: 'databricks.com',
    roleTitle: 'Forward Deployed Engineer',
    headline: 'The largest FDE org outside Palantir — 1,900+ customers served in its first year.',
    description: 'Databricks FDEs embed with customer teams to ship production data and AI systems — lakehouse migrations through first-of-a-kind GenAI apps (RAG, multi-agent, Text2SQL) built on Mosaic AI. Engagements are outcome-based with shared customer OKRs, not billable hours.',
    baseSalary: {min: 152900, max: 250208},
    totalComp: {min: 216000, max: 522000},
    compNote: 'Base: official posted bands, September 2026 (AI Engineer – FDE $152.9k–$210.2k; senior and federal reqs $182k–$250.2k). Total comp: levels.fyi Databricks Solution Architect proxy — median ≈ $363k.',
    locations: ['US metros', 'DC metro (federal)', 'London', 'Munich', 'Seoul', 'Amsterdam'],
    locationSummary: 'US metros · remote-eligible',
    arrangement: 'Remote-eligible on many reqs · customer on-sites every 4–8 weeks',
    experience: '5–7+ years, production GenAI experience',
    newGradFriendly: false,
    remoteFriendly: true,
    activelyHiring: true,
    hiringStatus: '98 open FDE reqs worldwide (62 US), incl. federal and national-security tracks',
    interviewProcess: [
      'Recruiter screen',
      'Coding screen',
      'Spark / distributed-systems deep dive',
      'ML and platform round',
      'Behavioral + hiring manager final — 4–7 weeks',
    ],
    programScale: 'Formal FDE org served 1,900+ customers in its first 12 months; raised $5B at a $190B valuation in Aug 2026, earmarked for AI agents',
    notableFact: 'After acquiring MosaicML, Databricks rebuilt delivery around FDEs with shared customer OKRs — engineers replaced consultant-style handoffs on GenAI work.',
    applyUrl: 'https://www.databricks.com/company/careers/open-positions',
    scores: {
      comp: 8, program: 8, brand: 8, momentum: 8,
    },
  }, {
    rank: 6,
    tier: 'A',
    company: 'Sierra',
    slug: 'sierra',
    guideSlug: 'sierra-agent-engineer-interview',
    companyDomain: 'sierra.ai',
    roleTitle: 'Software Engineer, Agent',
    headline: 'Bret Taylor’s rebrand of the FDE — $180k–$390k base to ship production agents for enterprise brands.',
    description: 'Sierra’s forward-deployed function is its Agent Engineering org — reqs post as “Software Engineer, Agent”: engineers own the Agent Development Life Cycle end to end, building and tuning production customer-facing AI agents for brands in finance, healthcare, telecom, and commerce. Deployed Infrastructure Engineers install the platform into customer VPCs.',
    baseSalary: {min: 180000, max: 390000},
    totalComp: null,
    compNote: 'Base: official posted SF band, September 2026 (specialist agent tracks $230k–$390k; new-grad $150k–$180k; London £150k–£315k); equity on top, unquantified. Sierra doesn’t publish totals.',
    locations: ['San Francisco', 'London', 'Singapore', '+ Madrid, Paris, Munich, Toronto, Sydney, Seoul'],
    locationSummary: 'SF · LDN · global',
    arrangement: 'In-person-first · regular customer-site work',
    experience: 'New grad (2027 track, added Aug 2026) through senior',
    newGradFriendly: true,
    remoteFriendly: false,
    activelyHiring: true,
    hiringStatus: '~30 open Software Engineer, Agent reqs across 9 cities, incl. new Munich and Seoul offices',
    interviewProcess: [
      'Recruiter screen',
      'Paid async work sample',
      'AI-native onsite: plan, build, and review a product live using coding agents',
      '60-minute system design',
      'Project deep dive + behavioral — 3–5 weeks',
    ],
    programScale: '~$16B company founded 2023 by Bret Taylor and Clay Bavor; agent engineering is the delivery model',
    notableFact: 'Sierra rebuilt its interview loop to be AI-native in January 2026 — candidates build with coding agents live in the onsite, mirroring the actual job.',
    applyUrl: 'https://sierra.ai/careers',
    scores: {
      comp: 9, program: 6, brand: 8, momentum: 8,
    },
  }, {
    rank: 7,
    tier: 'A',
    company: 'AWS',
    slug: 'aws',
    companyDomain: 'amazon.com',
    roleTitle: 'Forward Deployed Engineer',
    headline: 'A $1B bet: pods of FDEs shipping production agents inside enterprises in sprints of up to 45 days.',
    description: 'AWS’s Forward Deployed Engineering org embeds teams of five to six engineers inside enterprise customers for sprints of 45 minutes to 45 days (the “AI 45” model), co-building production agentic AI on Bedrock, Kiro, and Amazon Q — designed to leave customers self-sufficient, with runbooks, knowledge graphs, and trained staff.',
    baseSalary: {min: 143700, max: 297700},
    totalComp: null,
    compNote: 'Base: official amazon.jobs postings, September 2026 — FDE II (US Government, TS/SCI) $143.7k–$213.8k, Senior FDE $168.1k–$261.5k, Principal FDE to $297.7k in NY; sign-on + RSUs on top.',
    locations: ['San Francisco', 'New York', 'Austin', 'Seattle', '+ customer sites'],
    locationSummary: 'SF · NYC · AUS · SEA',
    arrangement: 'In-office default · embedded at customer sites',
    experience: '6+ years, including architecture leadership',
    newGradFriendly: false,
    remoteFriendly: false,
    activelyHiring: true,
    hiringStatus: 'Fresh posting wave (late Aug 2026) — ~10 FDE-org reqs from FDE II to Principal plus managers, incl. a new US Government (TS/SCI) team and a first Berlin req',
    interviewProcess: [
      'FDE-specific loop still not publicly documented as of September 2026',
      'Expect the standard Amazon loop: online assessment',
      'Phone screen',
      'Onsite: coding, system design, and Leadership Principles behavioral',
    ],
    programScale: '“Thousands of experts” planned; early customers include the NBA, NFL, Southwest Airlines, and Cox Automotive',
    notableFact: 'AWS pitches FDE engagements as outcome-based rather than billable hours — the anti-consulting consulting model, led by VP Francessca Vasquez.',
    applyUrl: 'https://www.amazon.jobs/en/search?base_query=forward+deployed+engineer',
    scores: {
      comp: 7, program: 6, brand: 9, momentum: 8,
    },
  }, {
    rank: 8,
    tier: 'A',
    company: 'Salesforce',
    slug: 'salesforce',
    companyDomain: 'salesforce.com',
    roleTitle: 'Forward Deployed Engineer, Agentforce',
    headline: 'FDE pods that move in with one customer for ~3 months and ship Agentforce agents to production.',
    description: 'Salesforce FDE pods embed full-time with a single enterprise customer for roughly three-month engagements, personally writing the code that designs, builds, and ships custom Agentforce agents into production. The program spans associate level through principal.',
    baseSalary: {min: 88970, max: 316750},
    totalComp: {min: 200000, max: 400000},
    compNote: 'Base: official Workday posting ranges, September 2026 ($88,970–$287,910 typical US; $97,860–$316,750 in CA/NY and select metros; Missionforce associate FDE $96.3k–$176.7k). Total comp: third-party estimate (2026 FDE comp reports).',
    locations: ['San Francisco', 'New York', 'Chicago', 'Austin', 'Atlanta', '+ hubs on six continents, some US-remote'],
    locationSummary: 'SF · NYC · 6 continents',
    arrangement: 'Hub hybrid, some US-remote · 25–50% travel',
    experience: 'Associate through principal — the Futureforce new-grad intake has lapsed',
    newGradFriendly: true,
    remoteFriendly: true,
    activelyHiring: true,
    hiringStatus: '~65 FDE-titled reqs across six continents, with new reqs posting weekly; India reqs have closed',
    interviewProcess: [
      'Recruiter screen',
      'Hiring manager round on AI project experience',
      'Technical round: agent design + coding on Agentforce patterns',
      'Panel: customer scenario + behavioral',
    ],
    programScale: 'Launched April 2025 and tripled inside six months; now hiring on six continents with new sub-specialties (Agentforce Orchestration, Data 360, Missionforce public sector)',
    notableFact: 'Salesforce brands FDE “today’s hottest role” in its own marketing — Benioff made FDEs the answer to the AI adoption gap at Dreamforce 2025.',
    applyUrl: 'https://salesforce.wd12.myworkdayjobs.com/External_Career_Site?q=forward%20deployed%20engineer',
    scores: {
      comp: 6, program: 7, brand: 7, momentum: 9,
    },
  }, {
    rank: 9,
    tier: 'A',
    company: 'Microsoft',
    slug: 'microsoft',
    companyDomain: 'microsoft.com',
    roleTitle: 'Forward Deployed Engineer (Frontier)',
    headline: 'The $2.5B answer, announced July 2: a 6,000-person subsidiary that embeds engineers inside enterprises.',
    description: 'Microsoft’s new Frontier Company is a dedicated subsidiary whose engineers sit inside client organizations to design, deploy, and run AI systems on-site. Posted FDE reqs build user-facing GenAI features end to end for major enterprise brands on the Copilot and Azure AI stack.',
    baseSalary: {min: 119800, max: 261000},
    totalComp: null,
    compNote: 'Base: official Microsoft FDE posting ranges from July 2026 ($120k–$235k typical US; $160k–$261k SF Bay/NYC); bonus + stock on top. The new Eightfold careers portal no longer exposes salary bands, so treat these as the last verified figures.',
    locations: ['US hubs', 'Tokyo', 'London', 'Netherlands', '+ embedded at customer sites'],
    locationSummary: 'US · Tokyo · UK',
    arrangement: 'On-site / embedded in customer environments',
    experience: '4–6+ years, production ML or LLM systems',
    newGradFriendly: false,
    remoteFriendly: false,
    activelyHiring: true,
    hiringStatus: '~22 FDE reqs across engineering, TPM, data science, and design — US, Tokyo, UK (clearance roles), Netherlands',
    interviewProcess: [
      'FDE-specific loop still not publicly documented as of September 2026',
      'Expect the standard Microsoft loop: recruiter screen',
      'Technical screen',
      'Onsite loop: coding, system design, behavioral',
    ],
    programScale: '$2.5B committed; ~6,000 engineers and sellers led by Rodrigo Kede Lima; early customers include LSEG, Unilever, and Novo Nordisk, with Accenture and Capgemini on the partner side',
    notableFact: 'Announced eight days after AWS’s $1B FDE org — the clearest sign yet that the industry sees deployment, not models, as the bottleneck.',
    applyUrl: 'https://apply.careers.microsoft.com/careers?query=forward%20deployed%20engineer&domain=microsoft.com',
    scores: {
      comp: 6, program: 5, brand: 9, momentum: 8,
    },
  }, {
    rank: 10,
    tier: 'B',
    company: 'Perplexity',
    slug: 'perplexity',
    companyDomain: 'perplexity.ai',
    roleTitle: 'Member of Technical Staff, Applied AI',
    headline: '$220k–$405k base under the research-lab “Member of Technical Staff” banner.',
    description: 'Perplexity’s FDEs — hired as Members of Technical Staff, Applied AI — design, build, and deploy end-to-end integrations that embed Perplexity into enterprise systems, owning deployments from architecture through production rollout and optimization.',
    baseSalary: {min: 220000, max: 405000},
    totalComp: null,
    compNote: 'Base: official posted bands plus equity, September 2026 — MTS Applied AI $220k–$405k; the new Perplexity Computer FDE req $200k–$250k. No published total-comp figures.',
    locations: ['New York', 'San Francisco', 'Palo Alto'],
    locationSummary: 'NYC · SF · Palo Alto',
    arrangement: 'Office hubs · travel varies by account',
    experience: '5+ years, production LLM systems',
    newGradFriendly: false,
    remoteFriendly: false,
    activelyHiring: true,
    hiringStatus: 'Hiring Applied AI MTS roles plus a new Perplexity Computer FDE req (Aug 2026)',
    interviewProcess: null,
    programScale: 'Early FDE function inside Applied AI, now extended by a dedicated Perplexity Computer FDE track',
    notableFact: 'Highest posted base band of the rising cohort — and FDEs carry the same “Member of Technical Staff” title as the researchers.',
    applyUrl: 'https://jobs.ashbyhq.com/Perplexity/3c656963-876a-458d-bca6-916a42a24c1a',
    scores: {
      comp: 9, program: 5, brand: 7, momentum: 6,
    },
  }, {
    rank: 11,
    tier: 'B',
    company: 'Ramp',
    slug: 'ramp',
    companyDomain: 'ramp.com',
    roleTitle: 'Software Engineer, Forward Deployed AI',
    headline: 'Fintech’s Palantir playbook — $189k–$330k base to build AI for Ramp’s biggest customers.',
    description: 'Ramp’s forward-deployed team designs end-to-end AI solutions for its largest enterprise customers, working directly with customers alongside sales and core product engineering. It’s the Palantir playbook applied to fintech, now productized as Ramp’s Applied AI Solutions offering for enterprise finance teams.',
    baseSalary: {min: 189000, max: 330000},
    totalComp: null,
    compNote: 'Base: official posted band plus equity, September 2026. Ramp doesn’t publish total-comp figures.',
    locations: ['New York'],
    locationSummary: 'NYC',
    arrangement: 'Office-oriented · relocation support to NYC',
    experience: '2+ years preferred, heavy AI-agent building',
    newGradFriendly: false,
    remoteFriendly: false,
    activelyHiring: true,
    hiringStatus: 'Actively hiring in NYC; the forward-deployed internship is no longer listed',
    interviewProcess: null,
    programScale: 'Fast-growing forward-deployed team at one of fintech’s biggest private companies',
    notableFact: 'Launched Applied AI Solutions in June 2026 — an embedded-FDE offering for enterprise finance teams — days after raising $750M at a $44B valuation.',
    applyUrl: 'https://jobs.ashbyhq.com/ramp/b614563f-3ce6-4dca-b5ba-0e5a6c8bda27',
    scores: {
      comp: 8, program: 5, brand: 7, momentum: 7,
    },
  }, {
    rank: 12,
    tier: 'B',
    company: 'Scale AI',
    slug: 'scale-ai',
    guideSlug: 'scale-ai-forward-deployed-engineer-interview',
    companyDomain: 'scale.com',
    roleTitle: 'Forward Deployed Engineer, GenAI',
    headline: 'FDEs whose customers include the frontier labs themselves — plus a cleared public-sector track.',
    description: 'Scale FDEs build customer-specific data infrastructure on the GenAI Data Engine — RLHF and evals pipelines — for frontier labs and enterprises, while Public Sector FDSEs deploy the platform inside secure federal environments.',
    baseSalary: {min: 179400, max: 292560},
    totalComp: null,
    compNote: 'Base: official posted bands, verified September 2026 — GenAI FDE $179k–$224k; Public Sector FDSE $184k–$293k. Equity on top, unquantified.',
    locations: ['San Francisco', 'New York', 'Washington DC', '+ federal sites'],
    locationSummary: 'SF · NYC · DC',
    arrangement: 'Hybrid (commercial) · onsite with 50%+ travel (public sector)',
    experience: '2+ years (GenAI track) — the lightest bar among major programs',
    newGradFriendly: false,
    remoteFriendly: false,
    activelyHiring: true,
    hiringStatus: '17 forward-deployed reqs open, incl. a Sr. Director of FDE and a new Frontier Agents track',
    interviewProcess: [
      'Recruiter screen',
      'Hiring manager call testing business sense and role fit',
      'Live practical coding (~45 minutes)',
      'Onsite: coding, ML/product, and customer-scenario rounds — ~4–5 weeks',
    ],
    programScale: 'FDE function since ~2023 across commercial GenAI and federal (Donovan); restructured after Meta’s $14.3B investment',
    notableFact: 'Its FDEs’ customers include the frontier labs themselves — and the public-sector track puts engineers inside secure government facilities.',
    applyUrl: 'https://job-boards.greenhouse.io/scaleai/jobs/4593571005',
    scores: {
      comp: 6, program: 7, brand: 7, momentum: 7,
    },
  }, {
    rank: 13,
    tier: 'B',
    company: 'Cursor',
    slug: 'cursor',
    companyDomain: 'cursor.com',
    roleTitle: 'Forward Deployed Engineer',
    headline: 'Embed with enterprise engineering orgs to ship production Cursor workflows — remote-friendly.',
    description: 'Cursor (Anysphere) FDEs embed with enterprise engineering teams to ship production workflows on Cursor itself — large-scale refactors, migration systems, automated PR-review loops, and spec-to-implementation pipelines. The org is hiring FDEs and FDE leadership across the US, Europe, and APAC.',
    baseSalary: null,
    totalComp: null,
    compNote: 'Cursor doesn’t post salary ranges; offers are reported top-of-market for the AI tooling category. Score reflects reported offers, not posted bands.',
    locations: ['San Francisco', 'New York', 'Remote (US)'],
    locationSummary: 'SF · NYC · Remote',
    arrangement: 'Remote-friendly · customer embeds',
    experience: '5+ years, plus 2+ years customer-facing',
    newGradFriendly: false,
    remoteFriendly: true,
    activelyHiring: true,
    hiringStatus: 'Six FDE reqs live across US, EMEA, and APAC, incl. a Regional Director and an RVP of FDE',
    interviewProcess: null,
    programScale: 'Founding-stage FDE org; Cursor became a wholly owned SpaceX subsidiary in August 2026, with business customers now ~80% of revenue',
    notableFact: 'Building a full FDE org, not a support function — Regional Director and RVP openings, a “Forward Deployed Strategist” track, and APAC expansion.',
    applyUrl: 'https://jobs.ashbyhq.com/cursor/34cecd0c-c392-4454-8ef5-261310541011',
    scores: {
      comp: 6, program: 5, brand: 8, momentum: 8,
    },
  }, {
    rank: 14,
    tier: 'B',
    company: 'Cohere',
    slug: 'cohere',
    companyDomain: 'cohere.com',
    roleTitle: 'Forward Deployed Engineer, Agentic Platform',
    headline: 'The broadest FDE footprint of the rising cohort — 16 open reqs across four continents, remote-friendly.',
    description: 'Cohere FDEs bridge the North agentic platform and enterprise engineering teams, integrating AI into finance, healthcare, telecom, and government with a security-first posture — including sovereign-AI and public-sector deployments.',
    baseSalary: {min: 140000, max: 325000},
    totalComp: null,
    compNote: 'Base: official posted US band, September 2026 ($140k–$325k in CA/NY/WA; $120k–$275k in other states; CA$175k–CA$385k in Canada) plus equity. Many non-US reqs still post no range.',
    locations: ['US remote', 'San Francisco', 'Toronto', 'Ottawa', '+ Europe, Middle East, Asia'],
    locationSummary: 'Remote (US) · global',
    arrangement: 'Remote-flexible · travel anticipated',
    experience: 'Senior-leaning — production Python, RAG, agents',
    newGradFriendly: false,
    remoteFriendly: true,
    activelyHiring: true,
    hiringStatus: '16 FDE reqs live across four continents, incl. sovereign AI and public-sector tracks',
    interviewProcess: null,
    programScale: 'Global FDE footprint including Sovereign AI and public-sector specialties, deepened by the Aleph Alpha merger',
    notableFact: 'Runs FDE specialties most programs don’t: sovereign-AI deployments, Korea/Japan/Singapore tracks, even security-cleared roles.',
    applyUrl: 'https://jobs.ashbyhq.com/cohere/b0bcef37-1d20-414f-aade-c54942d63df9',
    scores: {
      comp: 6, program: 6, brand: 7, momentum: 7,
    },
  }, {
    rank: 15,
    tier: 'B',
    company: 'Mistral',
    slug: 'mistral',
    companyDomain: 'mistral.ai',
    roleTitle: 'Forward Deployed Machine Learning Engineer',
    headline: 'Europe’s flagship FDE program — 11 open reqs from Palo Alto to Paris to Abu Dhabi.',
    description: 'Mistral’s customer-facing Applied AI org deploys its frontier models into enterprise production — fine-tuning, agents, and LLM applications from pre-sales through implementation — with a distinctly European footprint spanning sovereign and critical-infrastructure customers.',
    baseSalary: null,
    totalComp: null,
    compNote: 'Mistral doesn’t post salary ranges. US roles (Palo Alto, New York) are senior-leaning; European bands differ significantly.',
    locations: ['Palo Alto', 'New York', 'Paris', 'Munich', 'Montreal', 'Abu Dhabi'],
    locationSummary: 'Palo Alto · Paris · global',
    arrangement: 'Onsite Palo Alto · hybrid NY, Paris, Montreal',
    experience: '2+ years IC; 8+ years for technical leads',
    newGradFriendly: false,
    remoteFriendly: false,
    activelyHiring: true,
    hiringStatus: '11 forward-deployed reqs live globally, incl. four tech-lead reqs and a Paris FDE internship',
    interviewProcess: null,
    programScale: 'Europe’s largest FDE program, spanning sovereign institutions',
    notableFact: 'Posts roles the others don’t — “Critical and Sovereign Institutions” FDEs, and one of the only FDE internships anywhere (Paris).',
    applyUrl: 'https://jobs.ashbyhq.com/mistral.ai',
    scores: {
      comp: 5, program: 6, brand: 7, momentum: 7,
    },
  }, {
    rank: 16,
    tier: 'B',
    company: 'Snowflake',
    slug: 'snowflake',
    companyDomain: 'snowflake.com',
    roleTitle: 'Forward Deployed Engineer, Applied AI',
    headline: 'Back from our excluded list — FDE reqs reopened mid-2026 with a $236k–$339k Lead band.',
    description: 'Snowflake’s forward-deployed function re-emerged in 2026 across two tracks: Applied AI FDEs who embed with customers on Cortex-era AI workloads, and specialist Migration and Spark FDEs who move enterprise estates onto Snowflake. Reqs are centered on Menlo Park with a Warsaw outpost.',
    baseSalary: {min: 126000, max: 339250},
    totalComp: null,
    compNote: 'Base: official posted bands, September 2026 — Applied AI FDE $126k–$181.7k, Senior $200k–$287.5k, Lead Migration FDE $236k–$339.25k. Equity on top; no published totals.',
    locations: ['Menlo Park', 'Warsaw'],
    locationSummary: 'Menlo Park · Warsaw',
    arrangement: 'Menlo Park hub · customer-facing work',
    experience: 'Mid-level through lead',
    newGradFriendly: false,
    remoteFriendly: false,
    activelyHiring: true,
    hiringStatus: '5 FDE reqs live — Applied AI, Spark, and Migration tracks (two posted Aug 11)',
    interviewProcess: null,
    programScale: 'Revived function — FDE reqs closed mid-2026, then reopened April–August 2026 across three specialties',
    notableFact: 'The comeback story of the list: Snowflake sat on our excluded list in July with zero open FDE reqs — five are live now, with a posted Lead band of $236k–$339k.',
    applyUrl: 'https://careers.snowflake.com/us/en/search-results?keywords=forward%20deployed',
    scores: {
      comp: 7, program: 4, brand: 7, momentum: 6,
    },
  }, {
    rank: 17,
    tier: 'B',
    company: 'C3.ai',
    slug: 'c3-ai',
    companyDomain: 'c3.ai',
    roleTitle: 'Forward Deployed Engineer',
    headline: 'The other FDE pioneer — a decade of embedded delivery for industrials, energy, and defense.',
    description: 'C3 FDEs design integration architectures, write production code in customer environments, and act as the primary technical contact for enterprise accounts on the C3 Agentic AI Platform — industrials, energy, defense, and federal.',
    baseSalary: {min: 120000, max: 230000},
    totalComp: {min: 125000, max: 244000},
    compNote: 'Base: posted ranges across live reqs, September 2026 (FDE $120k–$158k; Director of FDE $180k–$230k). Total comp: Glassdoor estimate including equity, June 2026.',
    locations: ['Redwood City', 'New York', 'Chicago', 'Atlanta', 'Houston', 'Tysons Corner', 'London'],
    locationSummary: 'Bay Area · NYC · DC',
    arrangement: 'On-site · regular customer-site work',
    experience: '3+ years — Python/TypeScript, cloud, Kubernetes',
    newGradFriendly: false,
    remoteFriendly: false,
    activelyHiring: true,
    hiringStatus: '11 FDE reqs, incl. five new Director-level openings plus Federal and Defence tracks',
    interviewProcess: [
      'Recruiter screen',
      'HackerRank online assessment',
      'Hiring manager round',
      'Coding + system design round',
      'Behavioral final — ~4 weeks',
    ],
    programScale: 'FDE has been C3’s flagship delivery role for over a decade',
    notableFact: 'Pioneered enterprise FDE alongside Palantir years before the 2025–26 wave — though it now pays well below the new entrants.',
    applyUrl: 'https://c3.ai/careers/',
    scores: {
      comp: 4, program: 8, brand: 5, momentum: 5,
    },
  }, {
    rank: 18,
    tier: 'B',
    company: 'IBM',
    slug: 'ibm',
    companyDomain: 'ibm.com',
    roleTitle: 'Forward Deployed Engineer',
    headline: 'The mainframe-modernization FDE track — IBM Z and watsonx deployments, now senior-leaning.',
    description: 'IBM FDEs work directly with clients and product teams to deploy strategic IBM Z transaction-processing software and watsonx AI tooling from proof-of-value to production — mainframe modernization meets forward deployment.',
    baseSalary: null,
    totalComp: null,
    compNote: 'IBM careers pages don’t expose salary ranges, and the aggregator-sourced entry-level band from early 2026 died with the Durham posting. Score reflects IBM’s typical engineering bands, not posted figures.',
    locations: ['San Francisco', 'US multi-city', 'Seoul'],
    locationSummary: 'SF · US · Seoul',
    arrangement: 'Hybrid · client-facing deployment work',
    experience: 'Professional level — the entry-level and co-op tracks have closed',
    newGradFriendly: false,
    remoteFriendly: false,
    activelyHiring: true,
    hiringStatus: '4 FDE reqs — a senior SF role, two multi-city US reqs, and a Seoul Forward Deployed Architect',
    interviewProcess: [
      'FDE-specific loop not publicly documented',
      'Expect IBM standard: online assessment or HireVue video',
      'Technical interview(s)',
      'Hiring manager + behavioral — 3–6 weeks',
    ],
    programScale: 'Small emerging program centered on IBM Z + watsonx',
    notableFact: 'Ran the industry’s only FDE co-op in mid-2026; the student tracks have since closed, leaving a small professional-level FDE bench.',
    applyUrl: 'https://careers.ibm.com/en_US/careers/SearchJobs/forward%20deployed%20engineer',
    scores: {
      comp: 3, program: 4, brand: 6, momentum: 4,
    },
  }],
};

const inCities = (job, cities) => cities.some((city) => job.locations.join(' · ').includes(city));

// Collection sub-pages (/jobs/fde/<slug>). Each targets a long-tail query;
// `matches` selects which programs appear. The `salary` collection renders the
// full comp table instead of a filtered list.
export const collections = [{
  slug: 'san-francisco',
  name: 'San Francisco',
  kind: 'list',
  title: 'FDE Jobs in San Francisco: Salaries, Programs & Hiring',
  metaDescription: 'Every major Forward Deployed Engineer program hiring in San Francisco and the Bay Area — posted salary bands and live hiring status.',
  h1: 'FDE jobs in San Francisco',
  intro: 'San Francisco is the densest Forward Deployed Engineer market in the world. OpenAI, Anthropic, Sierra, and Scale AI run their forward-deployed orgs from SF proper, while Palantir and Mistral hire FDEs in Palo Alto, Google in Mountain View, Snowflake in Menlo Park, and C3.ai in Redwood City. Posted Bay Area base bands run from about $126k at the entry end to $400k+ at the AI-native startups — before equity.',
  matches: (job) => inCities(job, ['San Francisco', 'Palo Alto', 'Redwood City', 'Mountain View', 'Menlo Park']),
  faqItems: [{
    question: 'Which companies hire forward deployed engineers in San Francisco?',
    answer: 'In SF proper: OpenAI, Anthropic, Sierra, Scale AI, Salesforce, AWS, Cursor, Perplexity, Cohere, and IBM. Across the wider Bay Area: Palantir and Mistral (Palo Alto), Google (Mountain View), Snowflake (Menlo Park), and C3.ai (Redwood City). That is 15 of the 18 programs on The FDE List.',
  }, {
    question: 'How much do FDE jobs in San Francisco pay?',
    answer: 'Posted SF-market bands as of September 2026: OpenAI $162k–$280k base (FDSE reqs to $325k), Anthropic $280k–$320k (bonus-inclusive), Sierra $180k–$390k, Perplexity $220k–$405k, Snowflake $200k–$339k on its senior and lead tracks, and AWS $193k–$262k. At the frontier labs, total compensation with equity is estimated at $350k–$550k.',
  }, {
    question: 'Do San Francisco FDE roles require going into an office?',
    answer: 'Mostly yes. OpenAI is hybrid with three days a week in office, Anthropic expects 25%+ in office, and Sierra describes itself as primarily in-person. On top of office time, most programs expect 25–50% travel to customer sites.',
  }],
}, {
  slug: 'new-york',
  name: 'New York',
  kind: 'list',
  title: 'FDE Jobs in New York City: Salaries, Programs & Hiring',
  metaDescription: 'Every major Forward Deployed Engineer program hiring in New York City — posted salary bands and live hiring status.',
  h1: 'FDE jobs in New York',
  intro: 'New York is the other center of gravity for Forward Deployed Engineering. Palantir runs one of its largest FDE offices from NYC, Ramp is headquartered (and now hires exclusively) here, and OpenAI, Anthropic, AWS, Salesforce, Cursor, and Perplexity all hire FDEs into New York hubs. Posted NYC bands run from $135k (Palantir new grad) to $405k (the top of Perplexity’s band).',
  matches: (job) => inCities(job, ['New York']),
  faqItems: [{
    question: 'Which companies hire forward deployed engineers in New York?',
    answer: 'Twelve of the 18 programs on The FDE List hire in NYC: OpenAI, Palantir, Anthropic, Google, AWS, Salesforce, Ramp, Cursor, Perplexity, Scale AI, Mistral, and C3.ai. Ramp is headquartered in New York — and since mid-2026 hires its forward-deployed engineers only there.',
  }, {
    question: 'How much do FDE jobs in New York pay?',
    answer: 'Posted NYC-market bands as of September 2026: Palantir $135k–$200k base (levels.fyi total comp $155k–$295k), OpenAI $153k–$325k on its FDSE reqs, Salesforce up to $316.75k across levels, Ramp $189k–$330k, and Perplexity $220k–$405k.',
  }, {
    question: 'Is New York or San Francisco better for FDE jobs?',
    answer: 'They are close: the Bay Area has 15 hiring programs and New York 12. SF skews toward the frontier labs and AI-native startups; New York skews toward Palantir (its FDE center of gravity), fintech (Ramp), and enterprise/federal-adjacent work. Comp bands are broadly similar between the two.',
  }],
}, {
  slug: 'remote',
  name: 'Remote',
  kind: 'list',
  title: 'Remote FDE Jobs: Remote-Friendly Forward Deployed Roles',
  metaDescription: 'Forward Deployed Engineer roles you can do remotely — the programs that hire remote-eligible FDEs in the US, and what travel they still expect.',
  h1: 'Remote FDE jobs',
  intro: 'Forward-deployed work is customer-embedded by definition, so fully-remote FDE roles are rare — but they exist. Four major programs hire remote-eligible FDEs in the US, trading a daily office for periodic on-site embeds with customers. Expect travel to remain part of the deal: Databricks, for example, hires remote FDEs but brings them on-site with customers every four to eight weeks.',
  matches: (job) => job.remoteFriendly,
  faqItems: [{
    question: 'Can forward deployed engineers work remotely?',
    answer: 'Sometimes. Databricks hires remote-eligible FDEs across US metros, Cursor lists remote-US FDE roles, Cohere hires remote-eligible FDEs across the US, and some Salesforce Agentforce reqs are US-remote. Most other programs are hybrid or in-person-first.',
  }, {
    question: 'How much travel do remote FDE jobs involve?',
    answer: 'Remote-friendly does not mean travel-free. Databricks schedules customer on-sites every 4–8 weeks, Cohere anticipates regular customer travel, and industry-wide most FDE roles expect 25–50% time at customer sites.',
  }, {
    question: 'What is the best remote FDE job right now?',
    answer: 'By our index, Databricks — the largest FDE org outside Palantir (1,900+ customers served in its first year), remote-eligible on many reqs, with an estimated total comp of $216k–$522k.',
  }],
}, {
  slug: 'new-grad',
  name: 'New grad',
  kind: 'list',
  title: 'New Grad FDE Jobs: Entry-Level Forward Deployed Roles',
  metaDescription: 'The real entry-level paths into Forward Deployed Engineering — new-grad programs, internships, and co-ops, with posted starting salaries.',
  h1: 'New-grad FDE jobs',
  intro: 'Most FDE programs want 3–7 years of experience plus production LLM work — but three real onramps exist for students and new grads right now. Palantir is the deepest: intern → “Year at Palantir” → new-grad FDSE at $135k–$145k base. Sierra added a Software Engineer, Agent (New Grad 2027) req in August 2026 at $150k–$180k, and Salesforce hires at the associate level into its FDE pods. Google’s FDE I level and IBM’s co-op — real onramps earlier in 2026 — are currently between windows.',
  matches: (job) => job.newGradFriendly,
  faqItems: [{
    question: 'Can I become a forward deployed engineer straight out of school?',
    answer: 'Yes, at three programs right now: Palantir (new-grad FDSE, $135k–$145k posted base, plus intern tracks), Sierra (Software Engineer, Agent — New Grad 2027, $150k–$180k posted band, added August 2026), and Salesforce (associate-level FDE roles, incl. Missionforce at $96.3k–$176.7k). Google’s early-career FDE I band and IBM’s entry-level FDE track were live earlier in 2026 but are currently unposted.',
  }, {
    question: 'Are there FDE internships?',
    answer: 'Three real pipelines right now: Palantir’s FDSE internship and “Year at Palantir” program, Sierra’s Agent Development internships, and Mistral’s FDE internship in Paris. Ramp’s forward-deployed internship and IBM’s FDE co-op — both live in mid-2026 — are no longer posted.',
  }, {
    question: 'What should a new grad learn to land an FDE job?',
    answer: 'Full-stack fundamentals (Python and TypeScript dominate postings), production LLM basics — agents, RAG, evals — and customer-facing communication. Practice decomposition-style interviews: Palantir’s “decomp” round, where you break down a vague real-world system, is the archetypal new-grad FDE test.',
  }],
}, {
  slug: 'salary',
  name: 'Salary guide',
  kind: 'salary',
  title: `FDE Salary ${fdeJobs.lastUpdated.slice(0, 4)}: Forward Deployed Engineer Pay at ${fdeJobs.jobs.length} Companies`,
  metaDescription: 'FDE salary data for 2026: $190k median base, posted bands from $89k to $405k, and $350k–$550k estimated total comp at the frontier labs — every figure sourced.',
  h1: 'Forward Deployed Engineer salary, in real numbers',
  intro: 'Across 135 US FDE postings analyzed in mid-2026, the median base salary is about $190k, with most roles landing between $160k and $220k — and essentially none carrying a sales quota. The spread across programs is enormous: posted bands start near $89k (Salesforce’s typical-US floor) and reach $405k (Perplexity), while estimated total compensation at the frontier labs runs $350k–$550k. Every band below comes from an official posting, levels.fyi, or a dated public source.',
  matches: () => true,
  faqItems: [{
    question: 'What is the average forward deployed engineer salary?',
    answer: 'The median US base salary is roughly $190k (Recruiting from Scratch, June 2026, across 135 postings; the 25th–75th percentile range is $160k–$220k). A separate 1,000-posting analysis put the median at about $174k. Equity comes on top at nearly every program.',
  }, {
    question: 'Which company pays forward deployed engineers the most?',
    answer: 'Anthropic’s posted US band has the highest floor ($280k–$320k, inclusive of target bonus). On estimated total compensation, OpenAI and Anthropic lead at $350k–$550k. The widest posted band is the OpenAI Deployment Company’s $170k–$400k US FDE req; Perplexity posts to $405k and Sierra to $390k base.',
  }, {
    question: 'Do FDE offers include equity?',
    answer: 'Almost always — about 70% of postings mention equity explicitly. At OpenAI (PPUs) and Anthropic (RSU-heavy packages), equity can roughly double cash compensation; startups like Sierra, Ramp, and Perplexity grant meaningful but unquantified equity on top of posted bands.',
  }, {
    question: 'Do forward deployed engineers earn more than software engineers?',
    answer: 'At the same company, FDE bands generally match or slightly exceed core SWE bands, and they are compensated as engineering — an analysis of 1,000 postings found 0% carry sales quotas. The premium shows up at AI-native companies, where customer-embedded engineers are the revenue engine.',
  }],
}];

const year = fdeJobs.lastUpdated.slice(0, 4);

// Company-page FAQ (also emitted as FAQPage JSON-LD).
const companyFaqItems = (job, updatedLabel) => {
  const items = [];

  if (job.baseSalary || job.totalComp) {
    items.push({
      question: `How much does ${job.company} pay forward deployed engineers?`,
      answer: `${job.baseSalary ? `${job.company}’s posted base band is ${fmtRange(job.baseSalary)}.` : ''}${job.totalComp ? ` Estimated total compensation including equity is ${fmtRange(job.totalComp)}.` : ''} ${job.compNote}`.trim(),
    });
  } else {
    items.push({
      question: `How much does ${job.company} pay forward deployed engineers?`,
      answer: `${job.company} doesn’t publish salary ranges for this role. ${job.compNote}`,
    });
  }

  items.push({
    question: `Is ${job.company} hiring forward deployed engineers right now?`,
    answer: `As of ${updatedLabel}: ${job.hiringStatus}. Check the live careers page for current reqs.`,
  });

  items.push({
    question: `Does ${job.company} hire new grads as forward deployed engineers?`,
    answer: job.newGradFriendly
      ? `Yes — this is one of the few programs with a real early-career path. Experience expectation: ${job.experience}.`
      : `Not typically. Expected experience: ${job.experience}. For true entry-level paths, see our new-grad FDE jobs page.`,
  });

  return items;
};

// Role config consumed by JobsListPage. Strings here are the page copy; the
// components only add layout and the shared boilerplate.
const fde = {
  slug: 'fde',
  analyticsId: 'fde-jobs', // tracker page ids and ?src= values, unchanged since launch
  listName: 'The FDE List',
  roleName: 'Forward Deployed Engineer',
  roleShort: 'FDE', // mid-sentence: "Google FDE salary"
  roleShortTitle: 'FDE', // title case: breadcrumbs, link labels
  breadcrumbName: 'FDE Jobs',
  socialImage: '/images/fde-jobs-social.png',
  courseHref: '/courses/forward-deployed-engineering',
  questionsHref: '/questions',
  experienceLabel: 'Experience',
  tierTaglines: {
    S: 'The institutions — the programs that define the role, and pay accordingly',
    A: 'The scaled wave — platforms and hyperscalers building thousand-engineer FDE orgs',
    B: 'The rising programs — newer teams, founding energy, faster trajectories',
  },
  filters: [{
    name: 'All roles',
    value: 'all',
    predicate: () => true,
  }, {
    name: 'New-grad friendly',
    value: 'new-grad',
    predicate: (job) => job.newGradFriendly,
  }, {
    name: 'Remote friendly',
    value: 'remote',
    predicate: (job) => job.remoteFriendly,
  }, {
    name: 'Hiring now',
    value: 'hiring',
    predicate: (job) => job.activelyHiring,
  }],
  hub: {
    title: `FDE Jobs ${year}: ${fdeJobs.jobs.length} Forward Deployed Engineer Programs, Ranked`,
    // The page appends "Last verified <date>."
    metaDescription: `A living, ranked index of FDE jobs — Forward Deployed Engineer salaries, locations, open roles, and hiring status at ${fdeJobs.jobs.length} top companies.`,
    socialDescription: 'Every top Forward Deployed Engineer program, ranked — comp, hiring status, and open roles.',
    h1: 'The best Forward Deployed Engineer jobs, ranked.',
    intro: 'FDE is tech’s fastest-growing job title. This is the living index of the programs that matter — real compensation, live hiring status, and what the work is actually like, re-verified as the market moves. Every number traces to a posted range, a live careers page, or levels.fyi.',
    sourcesNote: 'Sources: posted salary ranges, levels.fyi, and company careers pages.',
    compIntro: 'Posted base-salary bands for each program’s core FDE role, US market — equity and bonus come on top, and at the frontier labs they can double the number. Bands span levels; senior hires land near the top.',
    about: {
      heading: 'What is a Forward Deployed Engineer?',
      paragraphs: [
        'A Forward Deployed Engineer (FDE) is a software engineer who works embedded with customers — shipping production code against real customer problems instead of a product roadmap. Palantir invented the role two decades ago; the AI wave made it the industry’s hottest job title, because getting LLMs to work inside a Fortune 500 is as much a deployment problem as a modeling one.',
        'The job blends software engineering with consulting-grade customer skills: scoping an ambiguous business problem, building the integration or agent that solves it, and demoing to executives — often on-site. It pays like engineering, promotes like sales, and compounds career capital faster than either.',
      ],
      guidesLead: 'New to the role? Start with our guides:',
      guides: [
        {label: 'What is an FDE?', href: '/blog/what-is-a-forward-deployed-engineer'},
        {label: 'FDE vs. Solutions Architect', href: '/blog/forward-deployed-engineer-vs-solutions-architect'},
        {label: 'the FDE resume guide', href: '/blog/forward-deployed-engineer-resume'},
      ],
    },
  },
  courseCallout: {
    title: 'Interviewing for FDE roles?',
    // Rendered after "<Brand>’s"
    body: 'Forward Deployed Engineering course covers the technical rounds, client scenarios, and decomposition interviews.',
  },
  finalCta: {
    body: 'Practice FDE interview questions, mock interview with peers, and learn the frameworks hiring managers actually score you on.',
    courseLabel: 'Take the FDE course',
  },
  companyPage: {
    title: (job, dataYear) => `${job.company} FDE Salary & Jobs (Forward Deployed Engineer, ${dataYear})`,
    metaDescription: (job, comp, updatedLabel) => `${job.company} forward deployed engineer salary${comp.value !== '—' ? ` (${comp.value} ${comp.label.replace(' (est.)', '')})` : ''}, locations, open roles, and live hiring status. Verified ${updatedLabel}.`,
  },
  companyFaqItems,
  lastUpdated: fdeJobs.lastUpdated,
  jobs: fdeJobs.jobs,
  collections,
  faqItems,
  marketStats,
  methodology,
  excludedNote,
};

export default fde;
